import React, { useState, useEffect } from 'react';
import { FaWhatsapp, FaLock, FaUsers, FaSync, FaSpinner, FaClock, FaSearch, FaPaperPlane } from 'react-icons/fa';
import axios from 'axios';

const ContactViewer = () => {
 const [isAuthenticated, setIsAuthenticated] = useState(false);
 const [pin, setPin] = useState('');
 const [pinError, setPinError] = useState('');
 const [sessionId, setSessionId] = useState('');
 const [contacts, setContacts] = useState([]);
 const [isLoading, setIsLoading] = useState(false);
 const [message, setMessage] = useState('');
 const [cooldownTime, setCooldownTime] = useState(0);
 const [totalContacts, setTotalContacts] = useState(0);
 const [searchTerm, setSearchTerm] = useState('');

 useEffect(() => {
   let interval;
   if (cooldownTime > 0) {
     interval = setInterval(() => {
       setCooldownTime(prev => prev - 1);
     }, 1000);
   }
   return () => clearInterval(interval);
 }, [cooldownTime]);

 const handlePinSubmit = async (e) => {
   e.preventDefault();
   setPinError('');

   try {
     const response = await axios.post('https://api.visiontech2030.com/api/contacts/verify-pin', {
       pin: pin
     });

     if (response.data.valid) {
       setIsAuthenticated(true);
       setSessionId(response.data.sessionId);
       setPin('');
       fetchContacts(response.data.sessionId);
     }
   } catch (error) {
     setPinError('Invalid PIN. Please try again.');
   }
 };

 const fetchContacts = async (currentSessionId = sessionId) => {
   if (!currentSessionId) return;

   setIsLoading(true);
   try {
     const response = await axios.get(
       `https://api.visiontech2030.com/api/contacts/fetch?sessionId=${currentSessionId}&limit=50`
     );

     const data = response.data;
     
     if (data.nextFetchAvailableIn > 0) {
       setCooldownTime(data.nextFetchAvailableIn);
     } else {
       setContacts(data.contacts);
       setTotalContacts(data.totalActiveContacts);
       setCooldownTime(0);
     }
   } catch (error) {
     console.error('Error fetching contacts:', error);
   } finally {
     setIsLoading(false);
   }
 };

 const handleWhatsAppClick = async (contact) => {
   if (!message.trim()) {
     alert('Please enter a message first');
     return;
   }

   try {
     const response = await axios.post('https://api.visiontech2030.com/api/contacts/whatsapp-url', {
       mobileNumber: contact.mobileNumber,
       message: message,
       contactName: contact.name || 'Contact'
     });

     window.open(response.data.whatsappUrl, '_blank');
   } catch (error) {
     console.error('Error generating WhatsApp URL:', error);
     alert('Failed to generate WhatsApp link');
   }
 };

 const filteredContacts = contacts.filter(contact =>
   contact.mobileNumber.includes(searchTerm) ||
   (contact.name && contact.name.toLowerCase().includes(searchTerm.toLowerCase()))
 );

 const formatCooldownTime = (seconds) => {
   const mins = Math.floor(seconds / 60);
   const secs = seconds % 60;
   return `${mins}:${secs.toString().padStart(2, '0')}`;
 };

 const styles = {
   container: {
     minHeight: '100vh',
     background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
     padding: '10px',
     fontFamily: "'Inter', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
     '@media (min-width: 768px)': {
       padding: '20px'
     }
   },
   authCard: {
     maxWidth: '400px',
     margin: '50px auto',
     backgroundColor: 'rgba(255, 255, 255, 0.95)',
     borderRadius: '20px',
     padding: '30px',
     boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
     textAlign: 'center',
     backdropFilter: 'blur(10px)',
     border: '1px solid rgba(255, 255, 255, 0.2)',
     '@media (min-width: 768px)': {
       margin: '100px auto',
       padding: '40px'
     }
   },
   authTitle: {
     fontSize: '20px',
     fontWeight: '700',
     color: '#2d3748',
     marginBottom: '8px',
     '@media (min-width: 768px)': {
       fontSize: '24px'
     }
   },
   authSubtitle: {
     fontSize: '14px',
     color: '#718096',
     marginBottom: '30px',
     '@media (min-width: 768px)': {
       fontSize: '16px'
     }
   },
   pinInput: {
     width: '100%',
     padding: '15px',
     margin: '20px 0',
     border: '2px solid #e2e8f0',
     borderRadius: '12px',
     fontSize: '18px',
     textAlign: 'center',
     outline: 'none',
     transition: 'all 0.3s ease',
     backgroundColor: 'rgba(255, 255, 255, 0.9)',
     letterSpacing: '4px',
     fontWeight: '600',
     boxSizing: 'border-box'
   },
   mainContainer: {
     maxWidth: '1400px',
     margin: '0 auto',
     backgroundColor: 'rgba(255, 255, 255, 0.95)',
     borderRadius: '20px',
     overflow: 'hidden',
     boxShadow: '0 25px 80px rgba(0,0,0,0.3)',
     backdropFilter: 'blur(10px)',
     border: '1px solid rgba(255, 255, 255, 0.2)'
   },
   header: {
     background: 'linear-gradient(135deg, #25d366 0%, #128c7e 100%)',
     color: 'white',
     padding: '20px',
     textAlign: 'center',
     position: 'relative',
     overflow: 'hidden',
     '@media (min-width: 768px)': {
       padding: '30px'
     }
   },
   headerOverlay: {
     position: 'absolute',
     top: 0,
     left: 0,
     right: 0,
     bottom: 0,
     background: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%239C92AC" fill-opacity="0.1"%3E%3Ccircle cx="30" cy="30" r="4"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
     opacity: 0.3
   },
   title: {
     fontSize: '24px',
     margin: '0 0 10px',
     display: 'flex',
     alignItems: 'center',
     justifyContent: 'center',
     gap: '12px',
     fontWeight: '700',
     position: 'relative',
     zIndex: 1,
     '@media (min-width: 768px)': {
       fontSize: '32px',
       gap: '15px'
     }
   },
   headerStats: {
     fontSize: '14px',
     opacity: 0.9,
     fontWeight: '500',
     position: 'relative',
     zIndex: 1,
     '@media (min-width: 768px)': {
       fontSize: '16px'
     }
   },
   messageSection: {
     padding: '20px',
     borderBottom: '1px solid #e2e8f0',
     background: 'linear-gradient(135deg, #f7fafc 0%, #edf2f7 100%)',
     '@media (min-width: 768px)': {
       padding: '25px'
     }
   },
   messageTitle: {
     fontSize: '18px',
     fontWeight: '600',
     color: '#2d3748',
     marginBottom: '15px',
     display: 'flex',
     alignItems: 'center',
     gap: '10px',
     '@media (min-width: 768px)': {
       fontSize: '20px'
     }
   },
   textarea: {
     width: '100%',
     padding: '15px',
     border: '2px solid #e2e8f0',
     borderRadius: '12px',
     resize: 'vertical',
     fontSize: '15px',
     outline: 'none',
     transition: 'all 0.3s ease',
     backgroundColor: 'white',
     fontFamily: 'inherit',
     boxSizing: 'border-box',
     minHeight: '80px',
     '@media (min-width: 768px)': {
       fontSize: '16px',
       minHeight: '100px'
     }
   },
   textareaHint: {
     color: '#718096',
     display: 'block',
     marginTop: '8px',
     fontSize: '12px',
     '@media (min-width: 768px)': {
       fontSize: '14px'
     }
   },
   controls: {
     padding: '20px',
     display: 'flex',
     flexDirection: 'column',
     gap: '15px',
     borderBottom: '1px solid #e2e8f0',
     background: 'white',
     '@media (min-width: 768px)': {
       flexDirection: 'row',
       alignItems: 'center',
       padding: '25px'
     }
   },
   searchContainer: {
     position: 'relative',
     flex: 1
   },
   searchIcon: {
     position: 'absolute',
     left: '15px',
     top: '50%',
     transform: 'translateY(-50%)',
     color: '#a0aec0',
     fontSize: '16px',
     zIndex: 1
   },
   searchInput: {
     width: '100%',
     padding: '12px 12px 12px 45px',
     border: '2px solid #e2e8f0',
     borderRadius: '12px',
     fontSize: '15px',
     outline: 'none',
     transition: 'all 0.3s ease',
     backgroundColor: '#f7fafc',
     boxSizing: 'border-box',
     '@media (min-width: 768px)': {
       fontSize: '16px'
     }
   },
   button: {
     padding: '12px 20px',
     backgroundColor: '#25d366',
     color: 'white',
     border: 'none',
     borderRadius: '12px',
     cursor: 'pointer',
     display: 'flex',
     alignItems: 'center',
     justifyContent: 'center',
     gap: '8px',
     fontSize: '14px',
     fontWeight: '600',
     transition: 'all 0.3s ease',
     boxShadow: '0 4px 15px rgba(37, 211, 102, 0.3)',
     minWidth: '160px',
     '@media (min-width: 768px)': {
       fontSize: '15px',
       padding: '14px 24px'
     }
   },
   disabledButton: {
     opacity: 0.6,
     cursor: 'not-allowed',
     transform: 'none',
     boxShadow: 'none'
   },
   contactsGrid: {
     display: 'grid',
     gridTemplateColumns: '1fr',
     gap: '15px',
     padding: '20px',
     background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
     '@media (min-width: 480px)': {
       gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
       gap: '20px'
     },
     '@media (min-width: 768px)': {
       gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
       gap: '25px',
       padding: '30px'
     }
   },
   contactCard: {
     border: '1px solid #e2e8f0',
     borderRadius: '16px',
     padding: '20px',
     backgroundColor: 'white',
     transition: 'all 0.3s ease',
     boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
     position: 'relative',
     overflow: 'hidden',
     '@media (min-width: 768px)': {
       padding: '25px'
     }
   },
   contactCardOverlay: {
     position: 'absolute',
     top: 0,
     right: 0,
     width: '60px',
     height: '60px',
     background: 'linear-gradient(135deg, #25d366 0%, #128c7e 100%)',
     borderRadius: '0 16px 0 40px',
     opacity: 0.1
   },
   contactMobile: {
     fontSize: '16px',
     fontWeight: '700',
     color: '#2d3748',
     marginBottom: '8px',
     position: 'relative',
     zIndex: 1,
     letterSpacing: '0.5px',
     '@media (min-width: 768px)': {
       fontSize: '18px'
     }
   },
   contactName: {
     fontSize: '13px',
     color: '#718096',
     marginBottom: '20px',
     position: 'relative',
     zIndex: 1,
     fontWeight: '500',
     '@media (min-width: 768px)': {
       fontSize: '14px',
       marginBottom: '25px'
     }
   },
   whatsappButton: {
     width: '100%',
     padding: '12px',
     backgroundColor: '#25d366',
     color: 'white',
     border: 'none',
     borderRadius: '12px',
     cursor: 'pointer',
     display: 'flex',
     alignItems: 'center',
     justifyContent: 'center',
     gap: '8px',
     fontSize: '14px',
     fontWeight: '600',
     transition: 'all 0.3s ease',
     position: 'relative',
     zIndex: 1,
     boxShadow: '0 4px 15px rgba(37, 211, 102, 0.3)',
     '@media (min-width: 768px)': {
       fontSize: '15px',
       padding: '14px'
     }
   },
   emptyState: {
     textAlign: 'center',
     padding: '40px 20px',
     color: '#718096',
     gridColumn: '1 / -1',
     '@media (min-width: 768px)': {
       padding: '60px 20px'
     }
   },
   emptyIcon: {
     fontSize: '48px',
     marginBottom: '15px',
     opacity: 0.5,
     '@media (min-width: 768px)': {
       fontSize: '64px',
       marginBottom: '20px'
     }
   },
   emptyTitle: {
     fontSize: '18px',
     fontWeight: '600',
     marginBottom: '8px',
     color: '#4a5568',
     '@media (min-width: 768px)': {
       fontSize: '22px'
     }
   },
   emptyText: {
     fontSize: '14px',
     '@media (min-width: 768px)': {
       fontSize: '16px'
     }
   },
   spinner: {
     animation: 'spin 1s linear infinite'
   }
 };

 // Add CSS animations
 useEffect(() => {
   const style = document.createElement('style');
   style.textContent = `
     @keyframes spin {
       0% { transform: rotate(0deg); }
       100% { transform: rotate(360deg); }
     }
     
     .contact-viewer input:focus,
     .contact-viewer textarea:focus {
       border-color: #25d366 !important;
       box-shadow: 0 0 0 3px rgba(37, 211, 102, 0.1) !important;
     }
     
     .contact-viewer button:hover:not(:disabled) {
       transform: translateY(-2px);
       box-shadow: 0 8px 25px rgba(37, 211, 102, 0.4) !important;
     }
     
     .contact-card:hover {
       transform: translateY(-5px);
       box-shadow: 0 15px 40px rgba(0,0,0,0.15) !important;
     }
     
     .whatsapp-btn:hover:not(:disabled) {
       background: linear-gradient(135deg, #20b858 0%, #0f7048 100%) !important;
     }
     
     @media (max-width: 767px) {
       .contact-viewer .controls {
         position: sticky;
         top: 0;
         z-index: 10;
         background: white;
         box-shadow: 0 2px 10px rgba(0,0,0,0.1);
       }
     }
   `;
   document.head.appendChild(style);
   return () => document.head.removeChild(style);
 }, []);

 if (!isAuthenticated) {
   return (
     <div style={styles.container} className="contact-viewer">
       <div style={styles.authCard}>
         <FaLock size={48} color="#667eea" style={{marginBottom: '20px'}} />
         <h2 style={styles.authTitle}>Access Contact Directory</h2>
         <p style={styles.authSubtitle}>Enter PIN to access WhatsApp contact database</p>
         
         <form onSubmit={handlePinSubmit}>
           <input
             type="password"
             value={pin}
             onChange={(e) => setPin(e.target.value)}
             placeholder="Enter PIN"
             maxLength="4"
             style={styles.pinInput}
             autoFocus
           />
           {pinError && <div style={{color: '#e53e3e', marginBottom: '15px', fontSize: '14px', fontWeight: '500'}}>{pinError}</div>}
           
           <button type="submit" style={styles.button}>
             <FaLock />
             Verify Access
           </button>
         </form>
       </div>
     </div>
   );
 }

 return (
   <div style={styles.container} className="contact-viewer">
     <div style={styles.mainContainer}>
       <div style={styles.header}>
         <div style={styles.headerOverlay}></div>
         <h1 style={styles.title}>
           <FaWhatsapp size={window.innerWidth < 768 ? 28 : 32} />
           Contact Directory
         </h1>
         <p style={styles.headerStats}>Total Contacts: {totalContacts.toLocaleString()} | Current Batch: {contacts.length}</p>
       </div>

       <div style={styles.messageSection}>
         <h3 style={styles.messageTitle}>
           <FaPaperPlane />
           Compose Message
         </h3>
         <textarea
           value={message}
           onChange={(e) => setMessage(e.target.value)}
           placeholder="Enter your WhatsApp message here..."
           style={styles.textarea}
           rows={window.innerWidth < 768 ? 3 : 4}
         />
         <small style={styles.textareaHint}>
           This message will be sent to contacts when you click WhatsApp button
         </small>
       </div>

       <div style={styles.controls}>
         <div style={styles.searchContainer}>
           <FaSearch style={styles.searchIcon} />
           <input
             type="text"
             value={searchTerm}
             onChange={(e) => setSearchTerm(e.target.value)}
             placeholder="Search contacts..."
             style={styles.searchInput}
           />
         </div>
         
         <button
           onClick={() => fetchContacts()}
           disabled={isLoading || cooldownTime > 0}
           style={{
             ...styles.button,
             ...(isLoading || cooldownTime > 0 ? styles.disabledButton : {})
           }}
         >
           {isLoading ? (
             <>
               <FaSpinner style={styles.spinner} />
               Loading...
             </>
           ) : cooldownTime > 0 ? (
             <>
               <FaClock />
               Wait {formatCooldownTime(cooldownTime)}
             </>
           ) : (
             <>
               <FaSync />
               Fetch New Contacts
             </>
           )}
         </button>
       </div>

       <div style={styles.contactsGrid}>
         {filteredContacts.length === 0 ? (
           <div style={styles.emptyState}>
             <FaUsers style={styles.emptyIcon} />
             <h3 style={styles.emptyTitle}>No Contacts Available</h3>
             <p style={styles.emptyText}>Click "Fetch New Contacts" to load contact data</p>
           </div>
         ) : (
           filteredContacts.map((contact) => (
             <div key={contact.id} style={styles.contactCard} className="contact-card">
               <div style={styles.contactCardOverlay}></div>
               <div style={styles.contactMobile}>{contact.mobileNumber}</div>
               <div style={styles.contactName}>{contact.name || 'No name'}</div>
               
               <button
                 onClick={() => handleWhatsAppClick(contact)}
                 disabled={!message.trim()}
                 style={{
                   ...styles.whatsappButton,
                   ...((!message.trim()) ? styles.disabledButton : {})
                 }}
                 className="whatsapp-btn"
               >
                 <FaWhatsapp />
                 Open WhatsApp
               </button>
             </div>
           ))
         )}
       </div>
     </div>
   </div>
 );
};

export default ContactViewer;